import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardBody } from '@/components/common/Card';
import Button from '@/components/common/Button';
import toast from 'react-hot-toast';
import { getPortalProfile, updatePortalProfile } from '@/services/affiliateApi';

export default function AffiliatePortalProfile() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    organization: '',
    website_url: '',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['portal-profile'],
    queryFn: getPortalProfile,
  });

  const profile = (data as any)?.profile;

  useEffect(() => {
    if (profile) {
      setFormData({
        full_name: profile.full_name || '',
        phone: profile.phone || '',
        organization: profile.organization || '',
        website_url: profile.website_url || '',
      });
    }
  }, [profile]);

  const updateMutation = useMutation({
    mutationFn: () => updatePortalProfile(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-profile'] });
      toast.success('Profile updated');
      setEditing(false);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Update failed'),
  });

  if (isLoading) return <div className="text-gray-500 p-8">Loading...</div>;
  if (!profile) return <div className="text-red-500 p-8">Failed to load profile</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
        {!editing && <Button variant="secondary" onClick={() => setEditing(true)}>Edit Profile</Button>}
      </div>

      <Card>
        <CardBody>
          {editing ? (
            <div className="space-y-4 max-w-lg">
              {(['full_name', 'phone', 'organization', 'website_url'] as const).map((field) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                    {field.replace(/_/g, ' ')}
                  </label>
                  <input
                    type="text"
                    value={formData[field]}
                    onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                  />
                </div>
              ))}
              <div className="flex gap-2 pt-2">
                <Button onClick={() => updateMutation.mutate()} disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button variant="secondary" onClick={() => { setEditing(false); setFormData({ full_name: profile.full_name || '', phone: profile.phone || '', organization: profile.organization || '', website_url: profile.website_url || '' }); }}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <dl className="grid grid-cols-2 gap-6 text-sm max-w-lg">
              <div><dt className="text-gray-500 font-medium">Name</dt><dd className="mt-1">{profile.full_name}</dd></div>
              <div><dt className="text-gray-500 font-medium">Email</dt><dd className="mt-1">{profile.email}</dd></div>
              <div><dt className="text-gray-500 font-medium">Phone</dt><dd className="mt-1">{profile.phone || 'Not set'}</dd></div>
              <div><dt className="text-gray-500 font-medium">Organization</dt><dd className="mt-1">{profile.organization || 'Not set'}</dd></div>
              <div><dt className="text-gray-500 font-medium">Type</dt><dd className="mt-1">{profile.organization_type || 'Not set'}</dd></div>
              <div><dt className="text-gray-500 font-medium">Website</dt><dd className="mt-1">{profile.website_url || 'Not set'}</dd></div>
              <div><dt className="text-gray-500 font-medium">Commission Rate</dt><dd className="mt-1">{(profile.commission_rate * 100).toFixed(0)}%</dd></div>
              <div><dt className="text-gray-500 font-medium">Stripe Connect</dt><dd className="mt-1">{profile.stripe_onboarding_complete ? 'Connected' : 'Not Connected'}</dd></div>
            </dl>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
